// Alpine Sleep Pro: scroll reveals, dual-zone temperature demo and season tabs.
// Idempotent so theme-editor section reloads re-bind cleanly.
(() => {
  document.documentElement.classList.add('as-js');

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        }
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 })
    : null;

  // Interpolates between cold and warm so the bed half shows the chosen temperature.
  const mix = (t) => {
    const cold = [47, 125, 225], warm = [232, 87, 42];
    const c = cold.map((v, i) => Math.round(v + (warm[i] - v) * t));
    return `rgb(${c[0]} ${c[1]} ${c[2]})`;
  };

  function bindDual(root) {
    if (root.dataset.asBound) return;
    root.dataset.asBound = '1';
    const min = Number(root.dataset.min), max = Number(root.dataset.max), split = Number(root.dataset.split);
    for (const input of root.querySelectorAll('input[type="range"]')) {
      const side = input.dataset.side;
      const out = root.querySelector(`[data-out="${side}"]`);
      const mode = root.querySelector(`[data-mode="${side}"]`);
      const half = root.querySelector(`[data-half="${side}"]`);
      const update = () => {
        const v = Number(input.value);
        out.textContent = `${v}°`;
        mode.textContent = v < split ? root.dataset.labelCool : root.dataset.labelWarm;
        half.style.setProperty('--as-zone', mix((v - min) / (max - min)));
        out.classList.toggle('as-ice', v < split);
        out.classList.toggle('as-ember', v >= split);
        input.setAttribute('aria-valuetext', `${v} °C, ${mode.textContent}`);
      };
      input.addEventListener('input', update);
      update();
    }
  }

  function bindTabs(root) {
    if (root.dataset.asBound) return;
    root.dataset.asBound = '1';
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const select = (tab, focus) => {
      for (const t of tabs) {
        const on = t === tab;
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
        root.querySelector(`#${t.getAttribute('aria-controls')}`).hidden = !on;
      }
      if (focus) tab.focus();
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (e) => {
        const keys = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: tabs.length - 1 };
        if (e.key in keys) { e.preventDefault(); select(tabs[(keys[e.key] + tabs.length) % tabs.length], true); }
      });
    });
  }

  // Night story: the moment crossing the middle of the viewport drives the sticky readout.
  function bindNight(root) {
    if (root.dataset.asBound || !('IntersectionObserver' in window)) return;
    root.dataset.asBound = '1';
    const time = root.querySelector('[data-night-time]');
    const temp = root.querySelector('[data-night-temp]');
    const moments = [...root.querySelectorAll('[data-night-moment]')];
    const activate = (m) => {
      for (const x of moments) x.classList.toggle('is-active', x === m);
      time.textContent = m.dataset.time;
      temp.textContent = m.dataset.temp;
      root.dataset.tone = m.dataset.tone;
    };
    if (moments[0]) activate(moments[0]);
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) activate(e.target);
    }, { rootMargin: '-45% 0px -45% 0px' });
    moments.forEach((m) => obs.observe(m));
  }

  // Sticky buy bar: visible after the first section, hidden while the buy section is on screen.
  function bindSticky(bar) {
    if (bar.dataset.asBound || !('IntersectionObserver' in window)) return;
    bar.dataset.asBound = '1';
    const first = document.querySelector('main .shopify-section, #MainContent .shopify-section');
    const buy = document.getElementById('kaufen');
    let pastFirst = false, buyVisible = false;
    const sync = () => {
      const show = pastFirst && !buyVisible;
      bar.classList.toggle('is-visible', show);
      bar.toggleAttribute('inert', !show);
      bar.setAttribute('aria-hidden', String(!show));
    };
    if (first) new IntersectionObserver(([e]) => { pastFirst = !e.isIntersecting && e.boundingClientRect.top < 0; sync(); }).observe(first);
    if (buy) new IntersectionObserver(([e]) => { buyVisible = e.isIntersecting; sync(); }).observe(buy);
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-as-reveal]').forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));
    scope.querySelectorAll('[data-as-dual]').forEach(bindDual);
    scope.querySelectorAll('[data-as-tabs]').forEach(bindTabs);
    scope.querySelectorAll('[data-as-night]').forEach(bindNight);
    scope.querySelectorAll('[data-as-sticky]').forEach(bindSticky);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  // Theme editor: show the selected tab / open the selected FAQ item.
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    if (el.getAttribute('role') === 'tab') el.click();
    if (el.tagName === 'DETAILS') el.open = true;
  });
})();
